import { Check, KeyRound, LoaderCircle, Trash2 } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import {
  apiClient,
  type UploadTemplateCredential,
  type UploadTemplateCredentialInput
} from "@/shared/api/api-client";
import { errorMessage } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { ConfirmDialog } from "@/shared/components/ConfirmDialog";
import {
  authKindsFor,
  SCRAPER_AUTH_KIND_LABELS,
  type ScraperAuthKind
} from "@/shared/vocabulary/scraper-auth-kinds";
import { Select } from "@/shared/components/Select";

/**
 * Credencial do scraper de um template (2026-10-16) — cadastro, e não
 * variável de ambiente. O tipo de autenticação decide os campos: login e
 * senha, ou token. "Nenhuma" é a AUSÊNCIA da credencial: o scraper usa o
 * que dispensa credencial (a Clipei, a página pública).
 *
 * Escrita PRÓPRIA, com botão próprio: template e credencial são duas
 * gravações (editar coluna não pode exigir redigitar senha). O segredo é
 * write-only — o campo nasce sempre vazio, o hint vem ao lado, e vazio
 * mantém o gravado; o hint nunca é reenviado.
 */
export function ScraperCredentialSection({
  token,
  templateId,
  scraper
}: {
  token: string;
  templateId: number;
  /** O scraper GRAVADO no template (o servidor valida contra ele). */
  scraper: string;
}) {
  const kinds = authKindsFor(scraper);
  const [stored, setStored] = useState<UploadTemplateCredential | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [kind, setKind] = useState<ScraperAuthKind | "">("");
  const [baseUrl, setBaseUrl] = useState("");
  const [login, setLogin] = useState("");
  const [secret, setSecret] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  const adopt = (credential: UploadTemplateCredential | null) => {
    setStored(credential);
    setKind((credential?.auth_kind as ScraperAuthKind | undefined) ?? "");
    setBaseUrl(credential?.base_url ?? "");
    setLogin(credential?.login ?? "");
    setSecret("");
  };

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      adopt(await apiClient.getUploadTemplateCredential(token, templateId));
    } catch (failure) {
      setLoadError(errorMessage(failure, "Não foi possível ler a credencial."));
    } finally {
      setLoading(false);
    }
  }, [token, templateId]);

  useEffect(() => {
    void load();
  }, [load]);

  const needsSecret = stored === null || stored.auth_kind !== kind;
  const canSave =
    kind !== "" &&
    !saving &&
    (kind !== "login_password" || login.trim() !== "") &&
    (!needsSecret || secret.trim() !== "");

  const save = async () => {
    if (kind === "") return;
    setSaving(true);
    setError(null);
    setNotice(null);
    const payload: UploadTemplateCredentialInput = {
      auth_kind: kind,
      base_url: baseUrl.trim() || null,
      login: kind === "login_password" ? login.trim() : null
    };
    // Vazio mantém o gravado: o campo nem viaja.
    if (secret.trim()) payload.secret = secret;
    try {
      adopt(await apiClient.setUploadTemplateCredential(token, templateId, payload));
      setNotice("Credencial gravada.");
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível gravar a credencial."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      await apiClient.deleteUploadTemplateCredential(token, templateId);
      adopt(null);
      setNotice("Credencial removida.");
    } catch (failure) {
      setError(errorMessage(failure, "Não foi possível remover a credencial."));
    } finally {
      setSaving(false);
      setConfirmingRemoval(false);
    }
  };

  return (
    <div className="card">
      <div className="card-title">
        <div>
          <KeyRound size={18} aria-hidden="true" />
          <h3>Credencial do scraper</h3>
          <p>
            Como o scraper de <strong>{scraper}</strong> entra na clipadora. Sem credencial, ele
            usa o que dispensa autenticação.
          </p>
        </div>
      </div>
      {loading && <ApiState kind="loading">Carregando a credencial…</ApiState>}
      {loadError && (
        <ApiState kind="error" onRetry={() => void load()}>
          <span role="alert">{loadError}</span>
        </ApiState>
      )}
      {!loading && !loadError && (
        <>
          {error && <p className="form-error" role="alert">{error}</p>}
          {notice && <p className="admin-field-help" role="status">{notice}</p>}
          <div className="admin-form-grid">
            <div className="admin-field-group">
              <label className="field">
                <span>Tipo de autenticação</span>
                <Select
                  value={kind}
                  onChange={event => {
                    setKind(event.target.value as ScraperAuthKind | "");
                    setSecret("");
                    setNotice(null);
                  }}
                >
                  <option value="">Nenhuma</option>
                  {kinds.map(option => (
                    <option key={option} value={option}>
                      {SCRAPER_AUTH_KIND_LABELS[option]}
                    </option>
                  ))}
                </Select>
              </label>
            </div>
            {kind !== "" && (
              <div className="admin-field-group">
                <label className="field">
                  <span>URL base (opcional)</span>
                  <input
                    value={baseUrl}
                    placeholder="https://painel.clipei.com.br"
                    maxLength={500}
                    onChange={event => setBaseUrl(event.target.value)}
                  />
                </label>
                <small className="admin-field-help">
                  Deixe em branco para usar o endereço padrão da clipadora.
                </small>
              </div>
            )}
            {kind === "login_password" && (
              <div className="admin-field-group">
                <label className="field">
                  <span>Login</span>
                  <input
                    value={login}
                    autoComplete="off"
                    maxLength={500}
                    onChange={event => setLogin(event.target.value)}
                  />
                </label>
              </div>
            )}
            {kind !== "" && (
              <div className="admin-field-group">
                <label className="field">
                  <span>{kind === "login_password" ? "Senha" : "Token"}</span>
                  <input
                    type="password"
                    value={secret}
                    autoComplete="new-password"
                    maxLength={500}
                    onChange={event => setSecret(event.target.value)}
                  />
                </label>
                <small className="admin-field-help">
                  {stored && stored.auth_kind === kind
                    ? <>Cadastrada: <code className="ai-provider-hint">{stored.secret_hint}</code>. Deixe em branco para manter.</>
                    : "Obrigatório: ainda não há um gravado neste tipo."}
                </small>
              </div>
            )}
          </div>
          <div className="admin-savebar-actions">
            {kind === "" && stored && (
              <button
                className="secondary-button"
                type="button"
                disabled={saving}
                onClick={() => setConfirmingRemoval(true)}
              >
                <Trash2 size={15} aria-hidden="true" /> Remover credencial
              </button>
            )}
            {kind !== "" && (
              <button
                className="primary-button"
                type="button"
                disabled={!canSave}
                onClick={() => void save()}
              >
                {saving ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}{" "}
                Salvar credencial
              </button>
            )}
          </div>
        </>
      )}
      {confirmingRemoval && (
        <ConfirmDialog
          title="Remover a credencial?"
          description="O scraper volta a usar o que dispensa autenticação. A senha ou o token gravado é apagado e não pode ser recuperado."
          confirmLabel="Remover credencial"
          danger
          loading={saving}
          onConfirm={() => void remove()}
          onCancel={() => setConfirmingRemoval(false)}
        />
      )}
    </div>
  );
}
